import moment from 'moment';

import { getConflictingItems } from '../Inventory.helpers';

import type { Workbook } from 'exceljs';
import type { AppSiteListExportData, AppSiteMarketplaceType, StrictExportRow } from '../../AppAndSiteMarketplace.types';

/** An item in both lists keeps only its final, overriding status: exclusion wins, so it is dropped from `include`. */
export const resolveExportLists = ({
  exclude,
  include
}: Pick<AppSiteListExportData, 'exclude' | 'include'>): Pick<AppSiteListExportData, 'exclude' | 'include'> => {
  const conflicts = getConflictingItems(include, exclude);
  return { exclude, include: conflicts.size ? include.filter(item => !conflicts.has(item)) : include };
};

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MAX_COLUMN_WIDTH = 80;

const getHeaders = (type: AppSiteMarketplaceType): string[] =>
  type === 'generic' ? ['Site'] : ['Search', 'Sonata ID'];

const getSheetRows = (
  items: string[],
  rows: StrictExportRow[] | undefined,
  type: AppSiteMarketplaceType
): string[][] => (type === 'specific' && rows ? rows.map(row => [row.search, row.key]) : items.map(item => [item]));

export const buildAppSiteListWorkbook = (
  workbook: Workbook,
  { exclude, excludeRows, include, includeRows, type }: AppSiteListExportData
) => {
  const headers = getHeaders(type);
  const sheets: [string, string[][]][] = [
    ['Include', getSheetRows(include, includeRows, type)],
    ['Exclude', getSheetRows(exclude, excludeRows, type)]
  ];

  for (const [name, rows] of sheets) {
    const sheet = workbook.addWorksheet(name);
    headers.forEach((header, index) => {
      // reduce instead of Math.max(...): spreading very large lists exceeds the max number of call arguments
      const longest = rows.reduce((max, row) => Math.max(max, row[index].length), header.length);
      sheet.getColumn(index + 1).width = Math.min(longest, MAX_COLUMN_WIDTH);
    });
    sheet.addRow(headers).font = { bold: true };
    for (const row of rows) {
      sheet.addRow(row);
    }
  }

  return workbook;
};

/** `app_site_list_<type>_<YYYYMMDD_HHmm>.xlsx` */
export const getAppSiteListFileName = (type: AppSiteMarketplaceType, date: Date = new Date()): string =>
  `app_site_list_${type}_${moment(date).format('YYYYMMDD_HHmm')}.xlsx`;

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/** Generates the Excel file in the browser (exceljs is lazy loaded) and downloads it. */
export const exportAppSiteList = async (data: AppSiteListExportData) => {
  const { Workbook: WorkbookCtor } = await import('exceljs');
  const workbook = buildAppSiteListWorkbook(new WorkbookCtor(), data);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), getAppSiteListFileName(data.type));
};
